import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import { FollowUpConfig, Contact, Category } from '../types';
import { Header } from '../components/Header';
import { ContactCategorySelector } from '../components/ContactCategorySelector';
import { useCreditConfirm } from '../components/credits/useCreditConfirm';

export function FollowUpPage() {
  const { request: requestFollowUpConfirm, modal: followUpConfirmModal } = useCreditConfirm();
  const [config, setConfig] = useState<FollowUpConfig | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [filters, setFilters] = useState({
    followUpAtivo: undefined as boolean | undefined,
    categoriaId: '',
    search: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadContacts();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [configData, categoriesData] = await Promise.all([
        apiService.getFollowUpConfig(),
        apiService.getAllCategories()
      ]);
      setConfig(configData);
      setCategories(categoriesData);
      await loadContacts();
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    try {
      const params: any = {};
      if (filters.followUpAtivo !== undefined) {
        params.followUpAtivo = filters.followUpAtivo;
      }
      if (filters.categoriaId) {
        params.categoriaId = filters.categoriaId;
      }
      if (filters.search) {
        params.search = filters.search;
      }

      const data = await apiService.getFollowUpContacts(params);
      setContacts(data);
    } catch (error) {
      console.error('Erro ao carregar contatos:', error);
      toast.error('Erro ao carregar contatos');
    }
  };

  const handleUpdateConfig = async (updates: Partial<FollowUpConfig>) => {
    if (!config) return;

    try {
      setLoadingConfig(true);
      const updated = await apiService.updateFollowUpConfig({ ...config, ...updates });
      setConfig(updated);
      toast.success('Configuração atualizada com sucesso');
    } catch (error) {
      console.error('Erro ao atualizar configuração:', error);
      toast.error('Erro ao atualizar configuração');
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleToggleContactFollowUp = async (contactId: string, currentValue: boolean) => {
    try {
      await apiService.updateContact(contactId, {
        nome: contacts.find(c => c.id === contactId)?.nome || '',
        telefone: contacts.find(c => c.id === contactId)?.telefone || '',
        followUpAtivo: !currentValue
      });
      
      setContacts(prev => prev.map(c => 
        c.id === contactId ? { ...c, followUpAtivo: !currentValue } : c
      ));
      
      toast.success(`Follow-up ${!currentValue ? 'ativado' : 'desativado'}`);
    } catch (error) {
      console.error('Erro ao atualizar follow-up:', error);
      toast.error('Erro ao atualizar follow-up');
    }
  };

  const handleDispararFollowUp = async (contactId: string, contactName: string) => {
    const contact = contacts.find(c => c.id === contactId);
    
    if (!contact?.followUpAtivo) {
      toast.error('Follow-up está inativo para este contato. Ative o follow-up antes de disparar.');
      return;
    }

    try {
      await apiService.dispararFollowUpManual(contactId);
      toast.success(`Follow-up enviado para ${contactName}`);
      
      // Recarregar contatos para atualizar ultimoFollowUpEnviadoEm
      await loadContacts();
    } catch (error: any) {
      console.error('Erro ao disparar follow-up:', error);
      const errorMessage = error?.message || 'Erro ao disparar follow-up';
      toast.error(errorMessage);
    }
  };

  const getTempoDecorrido = (ultimaMensagem: string | null) => {
    if (!ultimaMensagem) return '-';
    
    try {
      const now = new Date();
      const then = new Date(ultimaMensagem);
      const diffMs = now.getTime() - then.getTime();
      const diffMinutes = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMinutes < 60) {
        return `há ${diffMinutes} min`;
      } else if (diffHours < 24) {
        return `há ${diffHours}h`;
      } else {
        return `há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
      }
    } catch {
      return '-';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      {followUpConfirmModal}
      <Header
        title="Follow-up"
        subtitle="Configuração global e disparo manual por contato"
      />

      <div className="p-6 space-y-6">

      {/* Card de Configuração */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Configuração Global</h2>
            <p className="text-xs text-gray-500 mt-1">Controle o comportamento do follow-up automático para o tenant</p>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Automático</span>
            <button
              onClick={() => {
                if (config?.ativo) { handleUpdateConfig({ ativo: false }); return; }
                requestFollowUpConfirm({
                  title: 'Ativar follow-up automático por IA',
                  items: [{ featureKey: 'followup_ai_message', qty: 1, label: 'mensagem' }],
                  note: 'Enquanto ativo, o sistema gera e envia mensagens de follow-up por IA automaticamente aos contatos, consumindo este valor POR mensagem. Quanto mais contatos em follow-up, maior o gasto. Sem saldo, não envia.',
                  confirmLabel: 'Ativar follow-up',
                  onConfirm: () => handleUpdateConfig({ ativo: true }),
                });
              }}
              disabled={loadingConfig}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                config?.ativo ? 'bg-blue-600' : 'bg-gray-200'
              } ${loadingConfig ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config?.ativo ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
        
        <div className="space-y-4">
          {/* Tempo de Inatividade */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tempo de inatividade (horas)
            </label>
            <input
              type="number"
              min="1"
              value={config?.tempoInatividade || 24}
              onChange={(e) => handleUpdateConfig({ tempoInatividade: parseInt(e.target.value) })}
              disabled={loadingConfig}
              className="input-field w-32"
            />
            <p className="text-xs text-gray-500 mt-1">
              Após quanto tempo sem mensagem enviar follow-up
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-100 rounded-xl p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">📅 Horários e Dias de Follow-UP</h3>
                <p className="text-sm text-gray-600 mt-1">
                  ⚠️ Configure os dias da semana e horários permitidos para envio das mensagens. Os follow-ups serão pausados automaticamente fora desses períodos.
                </p>
              </div>
            </div>

            <div className="mt-4 space-y-4">
              {/* Horário de Funcionamento */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hora de Início
                  </label>
                  <input
                    type="time"
                    value={config?.horarioInicio || '09:00'}
                    onChange={(e) => handleUpdateConfig({ horarioInicio: e.target.value })}
                    disabled={loadingConfig}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hora de Fim
                  </label>
                  <input
                    type="time"
                    value={config?.horarioFim || '18:00'}
                    onChange={(e) => handleUpdateConfig({ horarioFim: e.target.value })}
                    disabled={loadingConfig}
                    className="input-field"
                  />
                </div>
              </div>

              {/* Dias da Semana */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dias da Semana
                </label>
                <div className="flex gap-2 flex-wrap">
                  {['seg', 'ter', 'qua', 'qui', 'sex', 'sab', 'dom'].map((dia) => (
                    <button
                      key={dia}
                      onClick={() => {
                        const current = config?.diasSemana || [];
                        const updated = current.includes(dia)
                          ? current.filter(d => d !== dia)
                          : [...current, dia];
                        handleUpdateConfig({ diasSemana: updated });
                      }}
                      disabled={loadingConfig}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                        config?.diasSemana?.includes(dia)
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                      } ${loadingConfig ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {dia}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status Follow-up
            </label>
            <select
              value={filters.followUpAtivo === undefined ? '' : filters.followUpAtivo.toString()}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                followUpAtivo: e.target.value === '' ? undefined : e.target.value === 'true'
              }))}
              className="input-field"
            >
              <option value="">Todos</option>
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
          </div>

          <ContactCategorySelector categories={categories} value={filters.categoriaId} onChange={(categoriaId) => setFilters((prev) => ({ ...prev, categoriaId }))} label="Filtrar por categoria" />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar
            </label>
            <input
              type="text"
              placeholder="Nome ou telefone..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="input-field"
            />
          </div>
        </div>
      </div>

      {/* Lista de Contatos */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Contatos em Follow-up ({contacts.length})
          </h2>
        </div>

        {contacts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Nenhum contato com última mensagem registrada
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Telefone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Categoria
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Última Mensagem
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Follow-up
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {contact.nome}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {contact.telefone}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {contact.categoria?.nome || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getTempoDecorrido(contact.ultimaMensagem || null)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleContactFollowUp(contact.id, contact.followUpAtivo || false)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          contact.followUpAtivo ? 'bg-green-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            contact.followUpAtivo ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleDispararFollowUp(contact.id, contact.nome)}
                        disabled={!contact.followUpAtivo}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                          contact.followUpAtivo
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        Fazer Follow-up
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
    </>
  );
}
